import { StateError } from '../errors';

// Pointy-top axial coordinates + the normative vertex/edge canonicalization (plan §3.2–3.3,
// machine-verified against pixel geometry in P0; pinned forever by T-MAP-001). Id formats are
// fixture vocabulary — tests write them literally:
//   HexId    "h{q},{r}"          e.g. "h-1,2"
//   VertexId "v{q},{r},{N|S}"    e.g. "v0,0,N"
//   EdgeId   "e{vidA}|{vidB}"    endpoints in tuple order (q, then r, then N<S)

export interface HexCoord {
  q: number;
  r: number;
}
export type HexId = string;
export type VertexId = string;
export type EdgeId = string;
export type CornerClass = 'N' | 'S';

/** The six hex directions, counterclockwise in world space with +y up (plan §6.5):
 *  E, NE, NW, W, SW, SE. Index into this table is the normative direction ordering. */
export const HEX_DIRS: readonly HexCoord[] = Object.freeze([
  Object.freeze({ q: 1, r: 0 }), // E
  Object.freeze({ q: 1, r: -1 }), // NE
  Object.freeze({ q: 0, r: -1 }), // NW
  Object.freeze({ q: -1, r: 0 }), // W
  Object.freeze({ q: -1, r: 1 }), // SW
  Object.freeze({ q: 0, r: 1 }), // SE
]);

/** Corner ring in edge-enumeration order (plan §3.3): N–NE–SE–S–SW–NW. */
export const CORNER_RING = Object.freeze(['N', 'NE', 'SE', 'S', 'SW', 'NW'] as const);
export type CornerName = (typeof CORNER_RING)[number];

export function hexId(q: number, r: number): HexId {
  return `h${q},${r}`;
}
export function parseHexId(id: HexId): HexCoord {
  const m = /^h(-?\d+),(-?\d+)$/.exec(id);
  if (!m) throw new StateError(`bad hex id: ${id}`);
  return { q: Number(m[1]), r: Number(m[2]) };
}
export function vertexId(q: number, r: number, c: CornerClass): VertexId {
  return `v${q},${r},${c}`;
}
export function parseVertexId(id: VertexId): { q: number; r: number; c: CornerClass } {
  const m = /^v(-?\d+),(-?\d+),(N|S)$/.exec(id);
  if (!m) throw new StateError(`bad vertex id: ${id}`);
  return { q: Number(m[1]), r: Number(m[2]), c: m[3] as CornerClass };
}

/** Total order on vertex ids: q, then r, then N < S (plan §3.3 — every tie-break reads this). */
export function compareVertexIds(a: VertexId, b: VertexId): number {
  const va = parseVertexId(a);
  const vb = parseVertexId(b);
  if (va.q !== vb.q) return va.q - vb.q;
  if (va.r !== vb.r) return va.r - vb.r;
  if (va.c === vb.c) return 0;
  return va.c === 'N' ? -1 : 1;
}

/** Canonical edge id from its two endpoint vertex ids (tuple-ordered; plan §3.3). */
export function edgeId(a: VertexId, b: VertexId): EdgeId {
  return compareVertexIds(a, b) <= 0 ? `e${a}|${b}` : `e${b}|${a}`;
}
export function parseEdgeId(id: EdgeId): [VertexId, VertexId] {
  const m = /^e(v-?\d+,-?\d+,[NS])\|(v-?\d+,-?\d+,[NS])$/.exec(id);
  if (!m) throw new StateError(`bad edge id: ${id}`);
  return [m[1]!, m[2]!];
}

export function axialDistance(a: HexCoord, b: HexCoord): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}
export function hexNeighbors(h: HexCoord): HexCoord[] {
  return HEX_DIRS.map((d) => ({ q: h.q + d.q, r: h.r + d.r }));
}

/** The plan §3.3 corner→canonical-vertex table (P0-verified geometry). */
const CANON: Readonly<Record<CornerName, (q: number, r: number) => VertexId>> = Object.freeze({
  N: (q, r) => vertexId(q, r, 'N'),
  NE: (q, r) => vertexId(q + 1, r - 1, 'S'),
  SE: (q, r) => vertexId(q, r + 1, 'N'),
  S: (q, r) => vertexId(q, r, 'S'),
  SW: (q, r) => vertexId(q - 1, r + 1, 'N'),
  NW: (q, r) => vertexId(q, r - 1, 'S'),
});

/** The named corner of a hex as its CANONICAL vertex id (plan §3.3 table). */
export function hexCornerVertex(q: number, r: number, corner: CornerName): VertexId {
  return CANON[corner](q, r);
}
/** All six corners in CORNER_RING order. */
export function hexCornerVertices(q: number, r: number): VertexId[] {
  return CORNER_RING.map((c) => CANON[c](q, r));
}
/** The six edges of a hex, from consecutive corner-ring pairs. */
export function hexEdgeIds(q: number, r: number): EdgeId[] {
  const corners = hexCornerVertices(q, r);
  return corners.map((v, i) => edgeId(v, corners[(i + 1) % 6]!));
}

/** The 3 hex coords touching a vertex (plan §3.3 table; grid-infinite). */
export function vertexHexes(v: VertexId): HexCoord[] {
  const { q, r, c } = parseVertexId(v);
  return c === 'N'
    ? [
        { q, r },
        { q, r: r - 1 },
        { q: q + 1, r: r - 1 },
      ]
    : [
        { q, r },
        { q, r: r + 1 },
        { q: q - 1, r: r + 1 },
      ];
}
/** The 3 grid-adjacent vertices (plan §3.3 table; board existence filtered by the graph). */
export function vertexNeighbors(v: VertexId): VertexId[] {
  const { q, r, c } = parseVertexId(v);
  return c === 'N'
    ? [vertexId(q, r - 1, 'S'), vertexId(q + 1, r - 1, 'S'), vertexId(q + 1, r - 2, 'S')]
    : [vertexId(q, r + 1, 'N'), vertexId(q - 1, r + 1, 'N'), vertexId(q - 1, r + 2, 'N')];
}
/** The two grid hexes an edge borders = the hexes adjacent to BOTH endpoints. */
export function edgeHexes(e: EdgeId): HexCoord[] {
  const [a, b] = parseEdgeId(e);
  const bKeys = new Set(vertexHexes(b).map((h) => `${h.q},${h.r}`));
  return vertexHexes(a).filter((h) => bKeys.has(`${h.q},${h.r}`));
}

/** All cells of the filled hexagon of the given radius, tuple-ordered (r, then q). */
export function hexagonCells(radius: number): HexCoord[] {
  const out: HexCoord[] = [];
  for (let r = -radius; r <= radius; r++) {
    const qMin = Math.max(-radius, -radius - r);
    const qMax = Math.min(radius, radius - r);
    for (let q = qMin; q <= qMax; q++) out.push({ q, r });
  }
  return out;
}

/**
 * Ring k enumerated counterclockwise from corner index c (into HEX_DIRS): start at k·D[c],
 * walk k steps per side in D[(c+side+2) mod 6] — the normative spiral/generator walk
 * (plan §6.5; hand-derivation in the T-MAP-004 fixture).
 */
export function ringCells(k: number, startCorner: number): HexCoord[] {
  if (k === 0) return [{ q: 0, r: 0 }];
  const c = ((startCorner % 6) + 6) % 6;
  const start = HEX_DIRS[c]!;
  let cur: HexCoord = { q: start.q * k, r: start.r * k };
  const out: HexCoord[] = [];
  for (let side = 0; side < 6; side++) {
    const d = HEX_DIRS[(c + side + 2) % 6]!;
    for (let step = 0; step < k; step++) {
      out.push(cur);
      cur = { q: cur.q + d.q, r: cur.r + d.r };
    }
  }
  return out;
}
